import React, { useCallback, useEffect, useState } from 'react';
import { Card, InputNumber, Select, Space, Table } from 'antd';
import PageHeader from '../../components/business/PageHeader';
import RequestState from '../../components/business/RequestState';
import { PageResult } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';

const emptyPage: PageResult<any> = { items: [], pageNo: 1, pageSize: 20, total: 0 };
const CommunicationsPage = () => {
    const [data, setData] = useState(emptyPage);
    const [filters, setFilters] = useState<any>({ pageNo: 1, pageSize: 20 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await v2Api.communications(filters));
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);
    useEffect(() => {
        load();
    }, [load]);
    const columns = [
        { title: '沟通时间', dataIndex: 'occurredAt', width: 170, render: formatDateTime },
        {
            title: '企业',
            dataIndex: 'companyName',
            width: 190,
            render: (v: string, row: any) => v || `企业 #${row.companyId}`,
        },
        {
            title: '方式 / 方向',
            key: 'channel',
            width: 130,
            render: (_: any, row: any) => `${row.channel || '—'} · ${row.direction || '—'}`,
        },
        {
            title: '主题与摘要',
            key: 'summary',
            render: (_: any, row: any) => (
                <div>
                    <strong>{row.subject || '未命名沟通'}</strong>
                    <div className="v2-muted v2-ellipsis">{row.summary}</div>
                </div>
            ),
        },
        { title: '沟通结果', dataIndex: 'resultCode', width: 120, render: (v: string) => v || '—' },
        {
            title: '下一步',
            key: 'next',
            width: 220,
            render: (_: any, row: any) => (
                <div>
                    {row.nextAction || '—'}
                    <div className="v2-muted">{formatDateTime(row.nextActionAt)}</div>
                </div>
            ),
        },
        {
            title: '执行人',
            dataIndex: 'executedByName',
            width: 100,
            render: (v: string, row: any) => v || row.executedBy || '—',
        },
    ];
    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="客户 · 人工沟通事实"
                title="沟通记录"
                description="同一条沟通记录通过关联关系出现在企业、联系人、商机和项目中；保存记录不会自动推进业务状态。"
            />
            <Card className="v2-filter-card">
                <Space className="v2-wrap-space">
                    <InputNumber
                        min={1}
                        placeholder="企业 ID"
                        onChange={(companyId) => setFilters({ ...filters, companyId, pageNo: 1 })}
                    />
                    <InputNumber
                        min={1}
                        placeholder="商机 ID"
                        onChange={(opportunityId) =>
                            setFilters({ ...filters, opportunityId, pageNo: 1 })
                        }
                    />
                    <InputNumber
                        min={1}
                        placeholder="项目 ID"
                        onChange={(projectId) => setFilters({ ...filters, projectId, pageNo: 1 })}
                    />
                    <Select disabled value="创建沟通需关联联系人" style={{ width: 210 }} />
                </Space>
            </Card>
            <Card className="v2-panel" bordered={false}>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!data.items.length}
                    onRetry={load}
                >
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data.items}
                        scroll={{ x: 1120 }}
                        pagination={{
                            current: data.pageNo,
                            pageSize: data.pageSize,
                            total: data.total,
                            onChange: (pageNo, pageSize) =>
                                setFilters({ ...filters, pageNo, pageSize }),
                        }}
                    />
                </RequestState>
            </Card>
        </div>
    );
};
export default CommunicationsPage;
